// Drawola Studio — i18n access.
//
// Fork-only. Same underlying `drawolaT()` as the multi-file store (see
// excalidraw-app/storage/i18n.ts); the difference is how we subscribe to
// language changes.
//
// `useDrawolaT()` from storage/i18n.ts hooks upstream's `useI18n()`, which is
// backed by a jotai-scope isolation that only exists INSIDE the `<Excalidraw>`
// tree. The Studio deliberately renders outside that tree (it is a full-screen
// overlay above the editor), so calling it here would throw "Missing Provider
// from createIsolation".
//
// Instead we subscribe to the app-level `appLangCodeAtom`, which lives in the
// app jotai store the Studio *is* inside, plus `drawolaLocaleVersionAtom`,
// which bumps when the app's own locale file finishes loading. Together they
// re-render the Studio with the new strings on a language switch, without the
// one-frame-of-English gap the langCode atom alone used to leave.

import { useAtomValue } from "../app-jotai";
import { appLangCodeAtom } from "../app-language/language-state";
import {
  readWelcomeDictionary,
  welcomeText,
} from "../app-language/welcomeDictionary";
import { drawolaLocaleCodeAtom, drawolaLocaleVersionAtom } from "../locales";
import { drawolaT } from "../storage/i18n";

type Replacement = { [key: string]: string | number };

export type StudioTranslate = (
  key: string,
  replacement?: Replacement,
) => string;

export const useStudioT = (): StudioTranslate => {
  // subscribe only — the returned function reads the module-level current
  // locale data, exactly like `drawolaT` does everywhere else.
  useAtomValue(appLangCodeAtom);
  useAtomValue(drawolaLocaleVersionAtom);
  return drawolaT;
};

/** Maps a drawola key the welcome uses to its welcome dictionary key. */
const welcomeDictionaryKey = (key: string): string =>
  key === "studio.language"
    ? "language"
    : key.replace(/^studio\.welcome\./, "");

/**
 * Translation for the welcome plus the code of the language it shows.
 *
 * When index.html carries the welcome dictionary, the welcome reads the
 * requested language from it at once, the same strings the inline script
 * painted, so the handover never falls back to English while the full locale
 * file loads. Without it, the welcome uses the app locale data and reports the
 * language that data is really in.
 */
export const useWelcomeText = (): { t: StudioTranslate; code: string } => {
  const t = useStudioT();
  const requested = useAtomValue(appLangCodeAtom);
  const shown = useAtomValue(drawolaLocaleCodeAtom);
  const strings = readWelcomeDictionary()?.strings[requested];
  if (!strings) {
    return { t, code: shown };
  }
  return {
    t: (key) => welcomeText(strings, welcomeDictionaryKey(key)),
    code: requested,
  };
};

/** Code of the language the app locale data is in right now. */
export const useShownLanguage = (): string =>
  useAtomValue(drawolaLocaleCodeAtom);
